import uuid from 'uuid';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import { TaskCreationParams, TaskData, TaskUpdateParams } from 'chaika-frontend-types/frontend-cms';

import { objectDifference } from '@utils/objectDifference';
import { removeUndefinedKeys } from '@utils/removeUndefinedKeys';
import { assertIsDefined } from '@utils/assertIsDefined';

import { TASK_UPDATE_KEYS, TaskAssigneeType } from '../constants';
import type { TaskFormState } from '../types';

import { startOfCurrentDayInTimezone } from './taskFormState';

export function getTaskCreationParams(state: TaskFormState): TaskCreationParams {
    assertIsDefined(state.requesterId, 'Task form sets the requester on init');
    assertIsDefined(state.urgency, 'Task form starts with the routine urgency');
    assertIsDefined(state.type, 'Task form starts with the call task type');

    return {
        id: uuid(),
        requesterId: state.requesterId,
        executorIds: resolveCreationExecutorIds(state),
        practiceIds: state.practiceIds,
        end: state.end?.toISOString(),
        start: (state.start ?? startOfCurrentDayInTimezone(state.timezone)).toISOString(),
        timezone: state.timezone,
        urgency: state.urgency,
        type: state.type,
        description: state.description,
        patientId: state.patientId,
        medicalCaseIds: state.medicalCaseIds ?? [],
        parentEntryId: state.entryId,
    };
}

export function getTaskUpdateParams(
    state: TaskFormState,
    task: TaskData,
): { id: string; updateParams: TaskUpdateParams } {
    const oldParams = pick(task, TASK_UPDATE_KEYS);
    const newParams = pick(state, TASK_UPDATE_KEYS);
    const updateParams = objectDifference(newParams, oldParams);

    const isStartDiffers = !task.start || new Date(task.start).getTime() !== state.start?.getTime();
    const isEndDiffers = !task.end || new Date(task.end).getTime() !== state.end?.getTime();
    const isMedicalCaseDiffers = !isEqual(
        [...(task.medicalCaseIds ?? [])].sort(),
        [...(state.medicalCaseIds ?? [])].sort(),
    );

    assertIsDefined(state.taskId, 'Task is updated only after it is saved');

    return {
        id: state.taskId,
        updateParams: removeUndefinedKeys<TaskUpdateParams>({
            ...updateParams,
            start: isStartDiffers ? state.start?.toISOString() : undefined,
            end: isEndDiffers ? state.end?.toISOString() : undefined,
            medicalCaseIds: isMedicalCaseDiffers ? (state.medicalCaseIds ?? null) : undefined,
        }),
    };
}

function resolveCreationExecutorIds({ assigneeType, executorIds, authUserId }: TaskFormState): string[] {
    if (assigneeType === TaskAssigneeType.Executors && isEmpty(executorIds)) {
        assertIsDefined(authUserId, 'Task form sets the auth user on init');

        return [authUserId];
    }

    return executorIds;
}
